import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Skeleton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { MdSend } from 'react-icons/md';
import toast from 'react-hot-toast';
import MuiAsyncAutocomplete from 'components/MuiAsyncAutocomplete';
import usePostAxiosData from 'hooks/usePostAxiosData';
import { useNotificationAttempts } from 'hooks/useNotificationScheduler';

const STATUS_COLORS = {
  SENT: 'success',
  PARTIALLY_SENT: 'warning',
  DELIVERY_REJECTED: 'warning',
  MISSING_EMAIL: 'error',
  INVALID_EMAIL: 'error',
  SERVICE_UNAVAILABLE: 'secondary',
  ATTACHMENT_ERROR: 'secondary',
  TEMPLATE_ERROR: 'error',
  PROCESSING_ERROR: 'default',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const HEADERS = [
  { id: 'attemptDate', label: 'Date' },
  { id: 'status', label: 'Statut' },
  { id: 'emailsSent', label: 'Envoyés' },
  { id: 'emailsFailed', label: 'Échecs' },
  { id: 'errorMessage', label: 'Message' },
];

export default function AttemptsPanel() {
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const searchId = selectedEnterprise?.value ?? null;

  const { data, isLoading, refetch: refetchAttempts } = useNotificationAttempts(searchId);
  const rows = data?.data ?? [];

  const [forceLoading, forceNotify] = usePostAxiosData(
    `enterprises/${searchId}/notify-employees-status`,
    'POST'
  );

  const handleForceNotify = async () => {
    await forceNotify({});
    toast.success(`Notification envoyée à "${selectedEnterprise?.label}"`);
    refetchAttempts();
  };

  return (
    <Paper elevation={2}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
          Tentatives par entreprise
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <MuiAsyncAutocomplete
            label="Entreprise"
            value={selectedEnterprise}
            format={{ value: 'id', label: 'socialRaison' }}
            fetch={'enterprises/search-all-by-term'}
            handleChange={(e, item) => setSelectedEnterprise(item)}
            wrapperClasses="flex-1"
          />
          {selectedEnterprise && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                forceLoading ? <CircularProgress size={16} color="inherit" /> : <MdSend />
              }
              disabled={forceLoading}
              onClick={handleForceNotify}
            >
              Notifier maintenant
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {HEADERS.map((h) => (
                <TableCell key={h.id} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!searchId ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    Sélectionnez une entreprise pour afficher les tentatives
                  </Typography>
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {HEADERS.map((h) => (
                    <TableCell key={h.id}>
                      <Skeleton width="80%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    Aucune tentative trouvée pour "{selectedEnterprise?.label}"
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((attempt, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(attempt.attemptDate)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={attempt.status}
                      color={STATUS_COLORS[attempt.status] ?? 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{attempt.emailsSent ?? 0}</TableCell>
                  <TableCell>{attempt.emailsFailed ?? 0}</TableCell>
                  <TableCell>
                    {attempt.errorMessage ? (
                      <Tooltip title={attempt.errorMessage}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                          }}
                        >
                          {attempt.errorMessage}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
